import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import endpoints from 'constants/endpoints';
import { safePostCall } from 'helpers/firebaseSetup';
import { parseZohoOkMessageOutput } from 'helpers/zoho/response-parsers';
import { getAppointmentsViewData } from 'state/actions/appointments';
import { showAlert } from 'state/actions/site';
import { isRecertificationRequest } from 'helpers/appointments/appointments';
import canSubmitOnboarding from './onboardingSubmittedEligibility';

const RESEND_LINK_SUBSTATUS =
  'Agent Confirmation of Contract Completion Needed';

const ACTIONS = [
  {
    label: 'Onboarding Submitted to Carrier for Approval',
    button: 'Onboarding Submitted',
    tooltip: 'Agent completed and submitted the carrier appointment request.',
    value: 'onboarding_submitted',
    acknowledgement:
      'I attest that I have submitted all relevant onboarding paperwork and attachments to the carrier for their review.'
  },
  {
    label: 'Request Link To Be Resent To Me',
    button: 'Request Link Resend',
    value: 'resend_link',
    acknowledgement:
      'I acknowledge that I did not receive the original contracting link and am requesting that it be resent. I understand the link will be delivered to the email address on file within 3–5 business days.'
  },
  {
    label: 'Close Appointment Request',
    button: 'Close Request',
    value: 'close_request',
    acknowledgement:
      'I acknowledge that this request will be closed and that no further action will be taken on this appointment unless a new request is submitted.',
    selectLabel: 'Why are you closing this request?',
    selectOptions: [
      {
        value: 'missing_required_documents',
        label: 'Missing required documents',
        helperTitle:
          'Your dedicated Contracting Team can help you get unblocked.',
        helperDescription:
          "Missing documents are the most common reason agents pause – and they're usually quick to resolve. Reach out before closing and we'll walk you through exactly what's needed.",
        helperFooter:
          "Don't close yet – reply to you appointment email or contact your O'Neill Contracting Team.",
        helperTitleColor: 'text-primary-darkened',
        helperDescriptionColor: 'text-primary-darkened',
        helperBgColor: 'bg-primary-darkened/10',
        helperBorderColor: 'border-primary-darkened',
        iconBgColor: 'bg-primary-darkened'
      },
      {
        value: 'no_longer_selling_with_this_carrier',
        label: 'No longer selling with this carrier',
        helperTitle:
          "Heads up – You'll need to restart if you change your mind.",
        helperDescription:
          "Closing this request removes it from your active pending appointments. If you decide to sell with this carrier later, you'll need to go through the full process again from the beginning.",
        helperFooter:
          'Consider keeping it open – restarting from scratch takes the same amount of time.',
        helperTitleColor: 'text-secondary-darkest',
        helperDescriptionColor: 'text-secondary-darkest',
        helperBgColor: 'bg-secondary-darkest/10',
        helperBorderColor: 'border-secondary-darkest',
        iconBgColor: 'bg-secondary-darkest'
      },
      {
        value: 'process_takes_too_long',
        label: 'Process takes too long',
        helperTitle: 'Most agents finish in under 10 minutes!',
        helperDescription:
          'Many agents fee the same way at first. The steps are straightforward once you get started – and our team is available to guid you through if you get stuck.',
        helperFooter:
          "Need help? Contact your O'Neill Contracting Team instead of closing.",
        helperTitleColor: 'text-pending',
        helperDescriptionColor: 'text-pending',
        helperBgColor: 'bg-pending/6',
        helperBorderColor: 'border-pending',
        iconBgColor: 'bg-pending'
      },
      {
        value: 'dont_want_to_pay_appointment_fees',
        label: "Don't want to pay appointment fees",
        helperTitle:
          "Good news — getting appointed now means you're ready to sell right away.",
        helperDescription:
          "Appointment fees are a one-time part of the process with this carrier, whether you complete it now or down the road. Finishing today means you're set up and earning commissions with this carrier sooner.",
        helperFooter:
          "You've got this — one step closer to being fully appointed!",
        helperTitleColor: 'text-secondary-darkened',
        helperDescriptionColor: 'text-secondary-darkened',
        helperBgColor: 'bg-secondary-darkened/10',
        helperBorderColor: 'border-secondary-darkened',
        iconBgColor: 'bg-secondary-darkened'
      },
      {
        value: 'dont_understand_next_steps',
        label: "Don't understand next steps",
        helperTitle:
          "We've got resources to help — you don't have to figure this out alone.",
        helperDescription:
          "Your portal has step-by-step instructions for this request, and a video walkthrough where available. They're quick to review and designed to make the process as easy as possible.",
        helperFooter:
          "Still have questions? Our contracting team is happy to help — just email us at <b>contracting@oneillmarketing.net</b> and we'll get you sorted.",
        helperTitleColor: 'text-primary-darkened',
        helperDescriptionColor: 'text-primary-darkened',
        helperBgColor: 'bg-primary-darkened/10',
        helperBorderColor: 'border-primary-darkened',
        iconBgColor: 'bg-primary-darkened'
      }
    ]
  }
];

const INITIAL_ERRORS = {
  username: '',
  password: ''
};

function getValidReprocessingStates(appt) {
  const allStates = appt.rawReprocessingStates;
  const uniqueStates = new Set(allStates || []);

  if (appt.carrier === 'BCBS')
    return uniqueStates.has(appt.uniqueBcbsState) ? [appt.uniqueBcbsState] : [];

  return (appt.appointments_information || [])
    .filter(row => uniqueStates.has(row.Appointment_State_1))
    .map(row => row.Appointment_State_1);
}

function getActionOptions(
  appt,
  carrierConsidersStates,
  validReprocessingStates
) {
  const isInProgress = ['In Progress', 'In Progress - 2nd Attempt'].includes(
    appt.appointment_status
  );

  const daysSinceInProgress =
    appt.in_progress_date?.length === 10
      ? Math.floor(
          (new Date().getTime() - new Date(appt.in_progress_date).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  const includedActions = [];
  if (isInProgress) {
    if (canSubmitOnboarding(appt)) includedActions.push('onboarding_submitted');
    if (daysSinceInProgress > 14) {
      if (
        appt.substatus === RESEND_LINK_SUBSTATUS &&
        ((!carrierConsidersStates && appt.allowTicketReprocess) ||
          validReprocessingStates.length > 0)
      ) {
        includedActions.push('resend_link');
      }
    }
  }

  if (!isRecertificationRequest(appt)) includedActions.push('close_request');

  return ACTIONS.filter(action => includedActions.includes(action.value));
}

export default function useAppointmentActions(
  appointmentDetails,
  carrierConsidersStates
) {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    username: appointmentDetails.username || '',
    password: appointmentDetails.password || ''
  });
  const [errors, setErrors] = useState({ ...INITIAL_ERRORS });
  const [action, setAction] = useState(null);
  const [isAcknowledgementOpen, setIsAcknowledgementOpen] = useState(false);
  const [isAcknowledgementAccepted, setIsAcknowledgementAccepted] =
    useState(false);
  const [closeRequestSelectValue, setCloseRequestSelectValue] = useState('');

  useEffect(() => {
    if (action?.value !== 'close_request') {
      setCloseRequestSelectValue('');
    }
  }, [action?.value]);

  const validReprocessingStates = carrierConsidersStates
    ? getValidReprocessingStates(appointmentDetails)
    : [];

  const actionOptions = getActionOptions(
    appointmentDetails,
    carrierConsidersStates,
    validReprocessingStates
  );

  const handleCredentialChange = (field, value) => {
    if (value.length > 255) {
      setErrors({
        ...errors,
        [field]: `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } must be less than 255 characters`
      });
      return;
    }
    setCredentials({
      ...credentials,
      [field]: value
    });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = async (e, onSuccess) => {
    e?.preventDefault?.();

    let isValid = true;
    if (!isAcknowledgementOpen) {
      const newErrors = { ...INITIAL_ERRORS };
      if (credentials.username.trim() === '') {
        newErrors.username = 'Username is required';
        isValid = false;
      }
      if (credentials.password.trim() === '') {
        newErrors.password = 'Password is required';
        isValid = false;
      }
      if (!isValid) {
        setErrors(newErrors);
        return;
      }
    }

    setIsLoading(true);
    try {
      const body = {
        isZohoRequest: true,
        requestId: appointmentDetails.id,
        endpoint: isAcknowledgementOpen
          ? endpoints.zoho.updateAppointmentFromPortal
          : endpoints.zoho.updateAppointmentCredentials
      };
      if (isAcknowledgementOpen) {
        body.action = action.value;
        if (action.value === 'resend_link') {
          body.reprocessing_states = validReprocessingStates;
        }
        if (action.value === 'close_request' && closeRequestSelectValue) {
          const closeReasonOption = action.selectOptions?.find(
            o => o.value === closeRequestSelectValue
          );
          if (closeReasonOption?.label) {
            body.close_request_reason = closeReasonOption.label;
          }
        }
      } else {
        body.username = credentials.username;
        body.password = credentials.password;
      }

      const response = await safePostCall(endpoints.awsProxy.safePost, body);

      if (response?.data?.details?.output) {
        const { ok, message } = parseZohoOkMessageOutput(
          response.data.details.output
        );
        if (message) {
          dispatch(
            showAlert({
              message,
              code: ok ? 'success' : 'error'
            })
          );
        }
        if (ok) {
          dispatch(
            getAppointmentsViewData(
              () => {},
              {},
              true,
              () => {
                if (onSuccess) onSuccess();
                setIsLoading(false);
              }
            )
          );
        } else {
          setIsLoading(false);
        }
      } else {
        dispatch(
          showAlert({
            message: 'Something went wrong. Try again later.',
            code: 'error'
          })
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      dispatch(
        showAlert({
          message: 'Something went wrong. Try again later.',
          code: 'error'
        })
      );
      setIsLoading(false);
    }
  };

  const resetAcknowledgement = () => {
    setIsAcknowledgementOpen(false);
    setIsAcknowledgementAccepted(false);
    setAction(null);
    setCloseRequestSelectValue('');
  };

  return {
    isLoading,
    credentials,
    errors,
    action,
    isAcknowledgementOpen,
    isAcknowledgementAccepted,
    actionOptions,
    validReprocessingStates,
    setAction,
    setIsAcknowledgementOpen,
    setIsAcknowledgementAccepted,
    closeRequestSelectValue,
    setCloseRequestSelectValue,
    handleCredentialChange,
    handleSubmit,
    resetAcknowledgement,
    resetCredentialErrors: () => setErrors({ ...INITIAL_ERRORS })
  };
}
